import * as React from 'react';
import { useTranslation } from 'react-i18next';

// Utils
import { useSelector } from 'react-redux';
import { IRootState } from '@src/store';

import {
  head,
  minutesToHoursAndMinutes,
  map,
  verifyProperty,
  flatten,
} from '@src/shared/src/util/general';
import {
  getLegsDuration,
  getMainLegs,
  getTariffsBookingCheckout,
  getTripFirstTS,
  getTripLastTS,
  getTripsVechiles,
} from '@src/shared/src/util/trips';
import { getTripOperatorImageURL } from '@toolkit/util/app';
import { formatDate } from '@src/shared/src/util/date';

import {
  DATE_FORMAT_TYPES,
  SEAT_RES_STATUS,
  STATUS,
  TERMS_CANCELLATION_STATUS,
  TERMS_REBOOKING_STATUS,
  VEHICLE_TYPES,
} from '@src/shared/src/const/app';
// Constants
// Actions
// Models
import { TripModel, BasketItemModel } from '@src/shared/src/models';
// Interfaces
import { ITariffBooking } from '@src/shared/src/interfaces';
// Components
import { CityInboundIcon } from '@toolkit/ui/icons/CityInboundIcon';
import FareTermsConditionsModal from '@pod/trips/components/FareTermsConditionsModal';
import ConfirmedBookingTransportSegment from './ConfirmedBookingTransportSegment';
import ConfirmedBookingHeader from './ConfirmedBookingHeader';
import ConfirmedBookingDocuments from './ConfirmedBookingDocuments';
import ConfirmedBookingTravelInfoItem from './ConfirmedBookingTravelInfoItem';
import Badge from '@toolkit/ui/Badge';

// Styles
import '../styles/ConfirmedBooking.scss';

type Props = {
  trip: TripModel;
  basketItems: BasketItemModel[];
  basketStatus: string;
};

const ConfirmedTrip: React.FC<Props> = (props) => {
  const { trip, basketItems, basketStatus } = props;
  const { t } = useTranslation();
  const profileLocale = useSelector((state: IRootState) =>
    verifyProperty('', state.adminUser.profile, ['preference', 'language']),
  );
  const isLocaleDE = profileLocale === 'de' || profileLocale === 'de-DE';

  const tariffBookings = getTariffsBookingCheckout(trip, basketItems);

  const tripMainLegs = getMainLegs(trip.legs);
  const tripDuration = getLegsDuration(tripMainLegs);
  const tripFirstTS = getTripFirstTS(tripMainLegs);
  const tripLastTS = getTripLastTS(tripMainLegs);
  const tripVehicles = getTripsVechiles([trip]);

  const getTitle = () => {
    if (tripVehicles.includes(VEHICLE_TYPES.PLANE)) {
      return `${t('bookingDetails.trip.title.planeTo')} ${tripLastTS?.arrName}`;
    }
    return `${t('bookingDetails.trip.title.tripTo')} ${tripLastTS?.arrName}`;
  };
  const tariff = tariffBookings ? head(tariffBookings) : null;
  const fareTerms = tariff?.fareTerms;
  const fareName = tariff?.fareName;

  const date = tripFirstTS?.depAt;

  return (
    <div className="confirmed-booking">
      <div className="confirmed-booking-inner">
        <ConfirmedBookingHeader
          title={getTitle()}
          image={getTripOperatorImageURL(trip)}
          date={formatDate(date, DATE_FORMAT_TYPES.DAY_LONG_DATE)}
          headerBadge={<Badge label={minutesToHoursAndMinutes(tripDuration)} icon={'timelapse'} />}
        />
        <ConfirmedBookingTransportSegment trip={trip} basketItems={basketItems} />
        {tariffBookings && (
          <ConfirmedBookingDocuments
            documents={flatten(
              map((tariffBooking: ITariffBooking) => tariffBooking.documents, tariffBookings),
            )}
            basketStatus={basketStatus}
          />
        )}
      </div>

      <div className="confirmed-booking-travel-info">
        <div className="confirmed-booking-travel-info-title">
          {t('BookingTravelInfo.title.booking')}
        </div>

        {tariffBookings && basketStatus === STATUS.CONFIRM_FINISHED && (
          <ConfirmedBookingTravelInfoItem
            icon={<i className="material-symbols">numbers</i>}
            title={t('bookingDetails.label.bookingId')}
            value={map(
              (tariffBooking: ITariffBooking) => tariffBooking.orderNumber,
              tariffBookings,
            )}
          />
        )}

        {tariff && fareTerms && (
          <>
            {fareTerms.handLuggage && (
              <ConfirmedBookingTravelInfoItem
                icon={<i className="material-symbols">carry_on_bag</i>}
                title={t('bookingDetails.label.handLuggage')}
                value={fareTerms.handLuggage}
              />
            )}
            {tripVehicles.includes(VEHICLE_TYPES.PLANE) && (
              <ConfirmedBookingTravelInfoItem
                icon={<i className="material-symbols">checked_bag</i>}
                title={t('bookingDetails.label.baggage')}
                value={fareTerms.baggage ? fareTerms.baggage : t('bookingDetails.value.noBaggage')}
              />
            )}
            {fareTerms.rebooking &&
              fareTerms.rebooking !== TERMS_REBOOKING_STATUS.REBOOKABLE_UNKNOWN && (
                <ConfirmedBookingTravelInfoItem
                  icon={<i className="material-symbols">restart_alt</i>}
                  title={t('bookingDetails.label.rebooking')}
                  value={
                    isLocaleDE ? fareTerms.deRebookingDescription : fareTerms.enRebookingDescription
                  }
                />
              )}
            {fareTerms.cancellation &&
              fareTerms.cancellation !== TERMS_CANCELLATION_STATUS.CANCELABLE_UNKNOWN && (
                <ConfirmedBookingTravelInfoItem
                  icon={<i className="material-symbols">send_money</i>}
                  title={t('bookingDetails.label.cancelation')}
                  value={
                    isLocaleDE
                      ? fareTerms.deCancellationDescription
                      : fareTerms.enCancellationDescription
                  }
                />
              )}
            {fareTerms.seatReservation == SEAT_RES_STATUS.AVAILABLE && (
              <ConfirmedBookingTravelInfoItem
                icon={<i className="material-symbols">airline_seat_recline_normal</i>}
                title={t('bookingDetails.label.seatReservation')}
                value={t('bookingDetails.value.available')}
              />
            )}
            {fareTerms.seatReservation == SEAT_RES_STATUS.INCLUDED && (
              <ConfirmedBookingTravelInfoItem
                icon={<i className="material-symbols">airline_seat_recline_normal</i>}
                title={t('bookingDetails.label.seatReservation')}
                value={t('bookingDetails.value.included')}
              />
            )}
            {fareTerms.deutschlandTicket && (
              <ConfirmedBookingTravelInfoItem
                icon={<i className="lp-icon-terms lp-icon-terms-deutschland-ticket-black-and-white" />}
                title={t('bookingDetails.label.deutschlandTicket')}
                value={t('bookingDetails.value.required')}
              />
            )}
            {fareTerms.depCityTicket && (
              <ConfirmedBookingTravelInfoItem
                icon={<CityInboundIcon />}
                title={t('bookingDetails.label.cityTicket')}
                value={
                  fareTerms.depCityTicket
                    ? t('bookingDetails.value.included')
                    : t('bookingDetails.value.notIncluded')
                }
              />
            )}
          </>
        )}


        {fareName && fareTerms && (
          <div className="confirmed-booking-travel-info-link">
            <FareTermsConditionsModal fareName={fareName} fareConditions={fareTerms.conditions} fareCancellations={fareTerms.cancellations} fareReissue={fareTerms.reissue} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfirmedTrip;
